import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { requestPasswordReset } from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/Form.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

const schema = z.object({ email: z.string().email('Enter a valid email') });
type FormValues = z.infer<typeof schema>;

interface Props { onNavigate: (path: string) => void }

export function ForgotPasswordPage({ onNavigate }: Props) {
  const { t } = useTranslation('authFlows');
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useFormError();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t('forgot.requestFailed'));
    }
  };

  return (
    <AuthLayout
      title={t('forgot.title')}
      subtitle={t('forgot.subtitle')}
      footer={
        <a href="/login" onClick={(e) => { e.preventDefault(); onNavigate('/login'); }}>
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('forgot.backToSignIn')}
        </a>
      }
    >
      {sent ? (
        <div className={formStyles.formSuccess} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t('forgot.success')}</span>
        </div>
      ) : (
        <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)}>
          {serverError && <FormError announce={serverError.nonce}>{serverError.message}</FormError>}
          <div className={formStyles.field}>
            <label className={formStyles.label}><Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('forgot.emailLabel')}</label>
            <input className={formStyles.input} type="email" autoComplete="email" placeholder={t('forgot.emailPlaceholder')} {...register('email')} />
            {errors.email && <span className={formStyles.errorMsg}>{errors.email.message}</span>}
          </div>
          <button type="submit" className={formStyles.btnPrimary} disabled={isSubmitting}>
            {isSubmitting ? t('forgot.submitting') : t('forgot.submit')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
